import 'leaflet/dist/leaflet.css';
import { useEffect } from 'react';
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { CargoEventDto, GeoTrackPointDto } from '@expeditor/shared';

// Fix дефолтных Leaflet-маркеров в Vite (иначе ломаются URL'ы CSS-фоновых иконок).
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: () => string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface Props {
  events: CargoEventDto[];
  track: GeoTrackPointDto[];
}

/**
 * Карта перевозки.
 *
 * - polyline по точкам трека (отсортированы по occurredAt — формирует пройденный путь).
 * - маркеры на точках события груза, у которых есть координаты.
 * - круглые маркеры на трек-точках (мелкие).
 *
 * Если нет ни трека, ни событий с координатами — карта центрирована на Москве.
 */
export function ShipmentMap({ events, track }: Props) {
  const eventPoints = events.filter(
    (e): e is CargoEventDto & { latitude: number; longitude: number } =>
      e.latitude !== null && e.longitude !== null,
  );

  const allCoords: [number, number][] = [
    ...track.map<[number, number]>((p) => [p.latitude, p.longitude]),
    ...eventPoints.map<[number, number]>((e) => [e.latitude, e.longitude]),
  ];

  const center: [number, number] =
    allCoords.length > 0 ? allCoords[Math.floor(allCoords.length / 2)] : [55.7558, 37.6173];
  const zoom = allCoords.length > 0 ? 6 : 5;

  return (
    // position+zIndex создаёт собственный контекст наложения, чтобы высокие
    // z-index слоёв/контролов Leaflet не перекрывали модалки (z-index ~200).
    <div style={{ height: 400, width: '100%', position: 'relative', zIndex: 0 }}>
      <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBoundsOnDataChange coords={allCoords} />
        {track.length >= 2 && (
          <Polyline
            positions={track.map((p) => [p.latitude, p.longitude])}
            pathOptions={{ color: 'blue', weight: 3 }}
          />
        )}
        {track.map((p) => (
          <CircleMarker
            key={p.id}
            center={[p.latitude, p.longitude]}
            radius={3}
            pathOptions={{ color: 'blue' }}
          >
            <Popup>
              {new Date(p.occurredAt).toLocaleString('ru-RU')}
              {p.speed !== null && <div>Скорость: {p.speed} км/ч</div>}
              {p.source && <div>Источник: {p.source}</div>}
            </Popup>
          </CircleMarker>
        ))}
        {eventPoints.map((e) => (
          <Marker key={e.id} position={[e.latitude, e.longitude]}>
            <Popup>
              <strong>{e.type}</strong>
              <br />
              {new Date(e.occurredAt).toLocaleString('ru-RU')}
              {e.locationName && <div>{e.locationName}</div>}
              {e.comment && <div style={{ marginTop: 4 }}>{e.comment}</div>}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

/** Подгоняет область карты под все точки. */
function FitBoundsOnDataChange({ coords }: { coords: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 0) return;
    if (coords.length === 1) {
      map.setView(coords[0], 12);
      return;
    }
    const bounds = L.latLngBounds(coords);
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [map, coords]);
  return null;
}
